(() => {
  const canonicalProductCache = new Map();

  const moneyText = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "string") return value;
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "";
    return `$${amount.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const productHandleFromUrl = (value) => {
    const match = String(value || "").match(/\/products\/([^/?#]+)/);
    return match ? match[1] : String(value || "").replace(/^\/products\//, "").replace(/[?#].*$/, "");
  };

  const productJsonPath = (handle) => {
    const safeHandle = productHandleFromUrl(handle).toLowerCase().replace(/[^a-z0-9-]/g, "");
    return safeHandle ? `/products/${safeHandle}.js` : "";
  };

  const productJsonToCard = (handle, json, fallback) => {
    const variants = Array.isArray(json.variants) ? json.variants : [];
    const variant = variants.find((candidate) => candidate.available) || variants[0] || {};
    let image = json.featured_image || (Array.isArray(json.images) ? json.images[0] : "") || fallback.image || "";
    if (image && typeof image === "object") image = image.src || image.url || "";
    const price = variant.price || json.price || "";
    const compare = variant.compare_at_price || json.compare_at_price || "";
    return {
      title: json.title || fallback.title,
      url: `/products/${json.handle || handle}`,
      image,
      price: price ? moneyText(Number(price) / 100) : moneyText(fallback.price),
      compareAtPrice: compare && Number(compare) > Number(price || 0) ? moneyText(Number(compare) / 100) : "",
      available: json.available !== false && variant.available !== false
    };
  };

  const fetchCanonicalProduct = (entry) => {
    const handle = entry.handle || productHandleFromUrl(entry.url);
    const jsonPath = productJsonPath(handle);
    if (!handle || !jsonPath) return Promise.resolve(entry);
    if (!canonicalProductCache.has(handle)) {
      canonicalProductCache.set(handle, fetch(jsonPath, {
        credentials: "same-origin"
      }).then((response) => {
        if (!response.ok) throw new Error("Product unavailable");
        return response.json();
      }).then((json) => productJsonToCard(handle, json, entry)).catch(() => ({
        title: entry.title,
        url: entry.url || `/products/${handle}`,
        image: entry.image || "",
        price: moneyText(entry.price),
        compareAtPrice: moneyText(entry.compareAtPrice),
        available: true
      })));
    }
    return canonicalProductCache.get(handle);
  };

  const buildCanonicalSolutionCard = (product) => {
    const article = document.createElement("article");
    article.className = "sa-solution-product";
    article.setAttribute("data-sa-canonical-product", "");

    const media = document.createElement("a");
    media.className = "sa-solution-product__media";
    media.href = product.url || "#";
    media.setAttribute("aria-label", `View ${product.title || "product"}`);
    if (product.image) {
      const image = document.createElement("img");
      image.src = product.image;
      image.alt = product.title || "";
      image.loading = "lazy";
      image.width = 260;
      image.height = 200;
      media.appendChild(image);
    } else {
      const placeholder = document.createElement("span");
      placeholder.className = "sa-solution-product__placeholder";
      placeholder.setAttribute("aria-hidden", "true");
      placeholder.textContent = "Image coming soon";
      media.appendChild(placeholder);
    }

    const body = document.createElement("div");
    body.className = "sa-solution-product__body";

    const title = document.createElement("a");
    title.className = "sa-solution-product__title";
    title.href = product.url || "#";
    title.textContent = product.title || "View product";

    const price = document.createElement("span");
    price.className = "sa-solution-product__price";
    price.textContent = product.price || "";

    body.append(title);
    if (product.price) body.append(price);
    if (product.available !== false) {
      const stock = document.createElement("span");
      stock.className = "sa-solution-product__stock";
      stock.textContent = "In stock";
      body.append(stock);
    }

    const link = document.createElement("a");
    link.className = "sa-solution-product__link";
    link.href = product.url || "#";
    link.textContent = "View details ->";
    body.append(link);

    article.append(media, body);
    return article;
  };

  const hydrateCanonicalDestinationProducts = (root = document) => {
    const payload = window.SecurityAustraliaDestinationRecommendations || {};
    const destinations = payload.destinations || {};
    root.querySelectorAll("[data-sa-solution-destination-panel][data-sa-destination-panel-handle]").forEach((panel) => {
      if (panel.dataset.saCanonicalDestinationProductsReady === "true") return;
      const handle = panel.getAttribute("data-sa-destination-panel-handle") || "";
      const destination = destinations[handle];
      const track = panel.querySelector("[data-sa-solution-products]");
      const entries = destination && destination.default && Array.isArray(destination.default.rankedProducts) ? destination.default.rankedProducts.slice(0, 4) : [];
      if (!track || !entries.length) return;
      panel.dataset.saCanonicalDestinationProductsReady = "true";
      Promise.all(entries.map(fetchCanonicalProduct)).then((products) => {
        const cards = products.filter(Boolean).map(buildCanonicalSolutionCard);
        if (!cards.length) return;
        track.replaceChildren(...cards);
      });
    });
  };

  const initIndustryHeroes = (root = document) => {
    root.querySelectorAll("[data-sa-industry-hero]").forEach((heroRegion) => {
      if (heroRegion.dataset.saReady === "true") return;

      const banners = heroRegion.querySelector("[data-sa-home-banners]");
      const tabs = Array.from(heroRegion.querySelectorAll("[data-sa-mega-tab]"));
      const panels = Array.from(heroRegion.querySelectorAll("[data-sa-mega-panel]"));
      if (!banners || !tabs.length || !panels.length) return;

      heroRegion.dataset.saReady = "true";
      let activeTab = null;
      let isLocked = false;
      let openTimer = null;
      let closeTimer = null;

      const isDesktop = () => window.matchMedia("(min-width: 761px)").matches;

      const clearTimers = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
      };

      const setBannersVisible = (isVisible) => {
        banners.hidden = !isVisible;
        banners.classList.toggle("is-active", isVisible);
      };

      const closeMenu = ({ focusTab = false, force = true } = {}) => {
        if (!force && isLocked) return;
        const tabToFocus = activeTab;
        activeTab = null;
        isLocked = false;
        clearTimers();

        tabs.forEach((tab) => {
          tab.classList.remove("is-active");
          tab.setAttribute("aria-expanded", "false");
        });

        panels.forEach((panel) => {
          panel.classList.remove("is-active");
          panel.hidden = true;
        });

        setBannersVisible(true);

        if (focusTab && tabToFocus) {
          tabToFocus.focus();
        }
      };

      const openMenu = (tab, { lock = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");
        clearTimers();
        activeTab = tab;
        isLocked = lock;
        setBannersVisible(false);

        tabs.forEach((candidate) => {
          const isActive = candidate.getAttribute("aria-controls") === panelId;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-expanded", isActive ? "true" : "false");
        });

        panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;
        });
      };

      const scheduleOpen = (tab) => {
        if (!isDesktop() || isLocked) return;
        window.clearTimeout(closeTimer);
        window.clearTimeout(openTimer);
        openTimer = window.setTimeout(() => openMenu(tab), 180);
      };

      const scheduleClose = () => {
        if (!isDesktop() || isLocked) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => closeMenu({ force: false }), 220);
      };

      const moveFocus = (current, direction) => {
        const index = tabs.indexOf(current);
        if (index === -1) return;
        const nextIndex = (index + direction + tabs.length) % tabs.length;
        tabs[nextIndex].focus();
      };

      tabs.forEach((tab) => {
        ["mouseenter", "pointerenter", "mouseover", "mousemove"].forEach((eventName) => {
          tab.addEventListener(eventName, () => scheduleOpen(tab));
        });
        tab.addEventListener("focus", () => {
          if (isDesktop()) openMenu(tab);
        });

        tab.addEventListener("click", () => {
          if (tab.getAttribute("aria-expanded") === "true" && isLocked) {
            closeMenu();
          } else {
            openMenu(tab, { lock: true });
          }
        });

        tab.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (tab.getAttribute("aria-expanded") === "true" && isLocked) {
              closeMenu({ focusTab: true });
            } else {
              openMenu(tab, { lock: true });
            }
          } else if (event.key === "ArrowDown" || event.key === "ArrowRight") {
            event.preventDefault();
            moveFocus(tab, 1);
          } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
            event.preventDefault();
            moveFocus(tab, -1);
          } else if (event.key === "Escape") {
            event.preventDefault();
            closeMenu({ focusTab: true });
          }
        });
      });

      ["mouseenter", "pointerenter", "mouseover"].forEach((eventName) => {
        heroRegion.addEventListener(eventName, () => {
          window.clearTimeout(closeTimer);
        });
      });

      heroRegion.addEventListener("mouseenter", () => {
        window.clearTimeout(closeTimer);
      });

      heroRegion.addEventListener("mouseleave", () => scheduleClose());

      panels.forEach((panel) => {
        panel.querySelectorAll("a").forEach((link) => {
          link.addEventListener("click", () => closeMenu());
        });
      });

      heroRegion.querySelectorAll("[data-sa-mega-close]").forEach((button) => {
        button.addEventListener("click", () => closeMenu({ focusTab: true }));
      });

      document.addEventListener("click", (event) => {
        if (activeTab && !heroRegion.contains(event.target)) {
          closeMenu();
        }
      });

      document.addEventListener("mousemove", (event) => {
        if (!activeTab || isLocked || !isDesktop()) return;
        if (heroRegion.contains(event.target)) {
          window.clearTimeout(closeTimer);
        } else {
          scheduleClose();
        }
      });

      document.addEventListener("keydown", (event) => {
        if (activeTab && event.key === "Escape") {
          closeMenu({ focusTab: true });
        }
      });

      closeMenu();
    });
  };

  const initBrandShowcases = (root = document) => {
    root.querySelectorAll("[data-sa-brand-showcase]").forEach((showcase) => {
      if (showcase.dataset.saBrandReady === "true") return;

      const tabs = Array.from(showcase.querySelectorAll("[data-sa-brand-tab]"));
      const panels = Array.from(showcase.querySelectorAll("[data-sa-brand-panel]"));
      if (!tabs.length || !panels.length) return;

      showcase.dataset.saBrandReady = "true";

      const activateTab = (tab, { focus = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");

        tabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-selected", isActive ? "true" : "false");
          candidate.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;
        });

        if (focus) tab.focus();
      };

      const moveTab = (currentTab, targetIndex) => {
        const nextTab = tabs[(targetIndex + tabs.length) % tabs.length];
        if (nextTab) activateTab(nextTab, { focus: true });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => activateTab(tab));

        tab.addEventListener("keydown", (event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveTab(tab, index + 1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveTab(tab, index - 1);
          } else if (event.key === "Home") {
            event.preventDefault();
            moveTab(tab, 0);
          } else if (event.key === "End") {
            event.preventDefault();
            moveTab(tab, tabs.length - 1);
          }
        });
      });

      const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
      activateTab(selectedTab);
    });
  };

  const initSolutionNavigators = (root = document) => {
    root.querySelectorAll("[data-sa-solution-nav]").forEach((navigator) => {
      if (navigator.dataset.saSolutionReady === "true") return;

      const modeTabs = Array.from(navigator.querySelectorAll("[data-sa-solution-mode-tab]"));
      const modePanels = Array.from(navigator.querySelectorAll("[data-sa-solution-mode-panel]"));
      if (!modeTabs.length || !modePanels.length) return;

      navigator.dataset.saSolutionReady = "true";
      const launcherButtons = Array.from(document.querySelectorAll("[data-sa-solution-launcher]"));
      const previewLaunchers = Array.from(document.querySelectorAll("[data-sa-solution-preview]"));
      const overlayTriggers = [...launcherButtons, ...previewLaunchers];
      const closeButtons = Array.from(navigator.querySelectorAll("[data-sa-solution-close]"));
      let activeLauncher = null;
      let openTimer = null;
      let closeTimer = null;
      let isLockedOpen = false;
      let suppressPreviewHoverUntil = 0;

      const destinationTabsForPanel = (panel) => Array.from(panel.querySelectorAll("[data-sa-solution-tab]"));
      const destinationPanelsForPanel = (panel) => Array.from(panel.querySelectorAll("[data-sa-solution-destination-panel]"));
      const escapeSelectorId = (value) => {
        if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(value);
        return String(value).replace(/["\\]/g, "\\$&");
      };
      const isDesktop = () => window.matchMedia("(min-width: 761px)").matches;
      const clearOverlayTimers = () => {
        window.clearTimeout(openTimer);
        window.clearTimeout(closeTimer);
      };
      const getModeTabByLauncher = (launcher) => {
        const mode = launcher && launcher.dataset ? (launcher.dataset.saSolutionPreviewMode || launcher.dataset.saSolutionLauncher) : "";
        const targetMode = mode === "area" ? "area" : "industry";
        return modeTabs.find((tab) => tab.id.includes(`mode-${targetMode}`)) || modeTabs[0];
      };
      const isHomepageRailLauncher = (launcher) => Boolean(launcher && launcher.closest && launcher.closest(".sa-hero__nav"));
      const isPreviewActionButton = (launcher) => Boolean(launcher && launcher.matches && launcher.matches("button[data-sa-solution-preview]"));
      const isShopByIndustryAnchor = (link) => {
        if (!link || !link.matches || !link.matches("a[href]")) return false;
        try {
          const target = new URL(link.getAttribute("href"), window.location.href);
          return target.origin === window.location.origin && target.pathname === window.location.pathname && target.hash === "#sa-shop-by-industry";
        } catch (error) {
          return false;
        }
      };
      const focusShopByIndustrySection = () => {
        const target = document.getElementById("sa-shop-by-industry");
        if (!target) return;
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
        target.scrollIntoView({
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          block: "start"
        });
        target.focus({ preventScroll: true });
      };
      const closeHeaderDrawerForAnchor = (link) => {
        const headerDrawer = link.closest("header-drawer");
        if (!headerDrawer) return false;
        if (typeof headerDrawer.closeMenuDrawer === "function") {
          headerDrawer.closeMenuDrawer();
          return true;
        }
        const details = headerDrawer.querySelector("details[open]");
        if (details) details.removeAttribute("open");
        document.body.style.removeProperty("top");
        document.body.classList.remove("fixed-position", "overflow-hidden-tablet");
        document.querySelector(".section-header")?.classList.remove("menu-open");
        return true;
      };
      const syncLauncherContext = () => {
        navigator.classList.toggle("is-homepage-rail-context", isHomepageRailLauncher(activeLauncher));
      };
      const positionOverlay = () => {
        const header = document.querySelector(".shopify-section-group-header-group") || document.querySelector("header");
        const headerRect = header ? header.getBoundingClientRect() : { bottom: 0 };
        const headerBottom = Math.max(0, Math.round(headerRect.bottom));
        const previewRail = activeLauncher && activeLauncher.closest ? activeLauncher.closest(".sa-hero__nav") : null;

        if (previewRail && window.matchMedia("(min-width: 1181px)").matches) {
          const railRect = previewRail.getBoundingClientRect();
          const gap = 14;
          const rightGutter = 24;
          const top = Math.max(headerBottom, Math.round(railRect.top));
          const left = Math.max(rightGutter, Math.round(railRect.right + gap));
          const width = Math.max(640, Math.round(window.innerWidth - left - rightGutter));

          navigator.style.setProperty("--sa-solution-overlay-top", `${top}px`);
          navigator.style.setProperty("--sa-solution-overlay-left", `${left}px`);
          navigator.style.setProperty("--sa-solution-overlay-width", `${width}px`);
          navigator.style.setProperty("--sa-solution-overlay-transform", "none");
          return;
        }

        const top = headerBottom;
        navigator.style.setProperty("--sa-solution-overlay-top", `${top}px`);
        navigator.style.removeProperty("--sa-solution-overlay-left");
        navigator.style.removeProperty("--sa-solution-overlay-width");
        navigator.style.removeProperty("--sa-solution-overlay-transform");
      };
      const setLauncherState = (launcher, expanded) => {
        if (!launcher) return;
        launcher.setAttribute("aria-expanded", expanded ? "true" : "false");
        if (launcher.matches && launcher.matches("[data-sa-solution-preview]")) {
          launcher.classList.toggle("is-active", expanded);
        }
        if (expanded) {
          launcher.setAttribute("aria-controls", navigator.id || "");
        }
      };
      const closeOverlay = ({ focusLauncher = false } = {}) => {
        clearOverlayTimers();
        navigator.classList.remove("is-overlay-open");
        navigator.setAttribute("aria-hidden", "true");
        document.documentElement.classList.remove("sa-solution-overlay-active");
        overlayTriggers.forEach((launcher) => setLauncherState(launcher, false));
        const launcherToFocus = activeLauncher;
        activeLauncher = null;
        syncLauncherContext();
        isLockedOpen = false;
        if (focusLauncher && launcherToFocus && !launcherToFocus.matches("[data-sa-solution-preview]")) {
          launcherToFocus.focus();
        }
      };
      let activateDestinationById = () => false;
      const openOverlay = (launcher, { lock = false } = {}) => {
        clearOverlayTimers();
        activeLauncher = launcher || activeLauncher || overlayTriggers[0] || null;
        syncLauncherContext();
        positionOverlay();
        navigator.classList.add("is-overlay-open");
        navigator.setAttribute("aria-hidden", "false");
        document.documentElement.classList.add("sa-solution-overlay-active");
        overlayTriggers.forEach((candidate) => setLauncherState(candidate, candidate === activeLauncher));
        const targetId = activeLauncher && activeLauncher.dataset ? activeLauncher.dataset.saSolutionTarget : "";
        if (!targetId || !activateDestinationById(targetId)) {
          const modeTab = getModeTabByLauncher(activeLauncher);
          if (modeTab) activateMode(modeTab);
        }
        isLockedOpen = lock;
      };
      const scheduleOverlayOpen = (launcher) => {
        if (!isDesktop() || isLockedOpen) return;
        if (launcher && launcher.matches && launcher.matches("[data-sa-solution-preview]") && Date.now() < suppressPreviewHoverUntil) return;
        window.clearTimeout(closeTimer);
        window.clearTimeout(openTimer);
        openTimer = window.setTimeout(() => openOverlay(launcher), 140);
      };
      const scheduleOverlayClose = () => {
        if (!isDesktop() || isLockedOpen) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => closeOverlay(), 240);
      };

      const syncSelect = (modePanel, panelId) => {
        const select = modePanel.querySelector("[data-sa-solution-select]");
        if (select) select.value = panelId;
      };

      const activateDestination = (modePanel, tab, { focus = false, updateHash = false } = {}) => {
        if (!modePanel || !tab) return;
        const panelId = tab.getAttribute("aria-controls");
        if (!panelId) return;

        destinationTabsForPanel(modePanel).forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-selected", isActive ? "true" : "false");
          candidate.tabIndex = isActive ? 0 : -1;
        });

        destinationPanelsForPanel(modePanel).forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;

          if (isActive) {
            const productTrack = panel.querySelector("[data-sa-solution-products]");
            if (productTrack) productTrack.scrollTo({ left: 0, behavior: "auto" });
          }
        });

        syncSelect(modePanel, panelId);

        if (updateHash && panelId && window.history && window.history.replaceState) {
          window.history.replaceState(null, "", `#${panelId.replace(/^sa-solution-/, "sa-")}`);
        }

        if (focus) tab.focus();
      };

      const activateMode = (tab, { focus = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");
        if (!panelId) return;

        modeTabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-selected", isActive ? "true" : "false");
          candidate.tabIndex = isActive ? 0 : -1;
        });

        modePanels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;

          if (isActive) {
            const activeDestination = panel.querySelector("[data-sa-solution-tab].is-active") || panel.querySelector("[data-sa-solution-tab]");
            if (activeDestination) activateDestination(panel, activeDestination, { updateHash: false });
          }
        });

        if (focus) tab.focus();
      };

      activateDestinationById = (panelId) => {
        if (!panelId) return false;
        const targetPanel = navigator.querySelector(`#${escapeSelectorId(panelId)}`);
        if (!targetPanel) return false;

        const containingModePanel = targetPanel.closest("[data-sa-solution-mode-panel]");
        if (!containingModePanel) return false;

        const modeTab = modeTabs.find((tab) => tab.getAttribute("aria-controls") === containingModePanel.id);
        const destinationTab = containingModePanel.querySelector(`[data-sa-solution-tab][aria-controls="${escapeSelectorId(targetPanel.id)}"]`);

        if (modeTab) activateMode(modeTab);
        if (destinationTab) activateDestination(containingModePanel, destinationTab, { updateHash: false });

        return Boolean(modeTab && destinationTab);
      };

      const moveMode = (targetIndex) => {
        const nextTab = modeTabs[(targetIndex + modeTabs.length) % modeTabs.length];
        if (nextTab) activateMode(nextTab, { focus: true });
      };

      modeTabs.forEach((tab, index) => {
        tab.addEventListener("click", () => activateMode(tab));

        tab.addEventListener("keydown", (event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveMode(index + 1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveMode(index - 1);
          } else if (event.key === "Home") {
            event.preventDefault();
            moveMode(0);
          } else if (event.key === "End") {
            event.preventDefault();
            moveMode(modeTabs.length - 1);
          }
        });
      });

      modePanels.forEach((modePanel) => {
        const tabs = destinationTabsForPanel(modePanel);
        const select = modePanel.querySelector("[data-sa-solution-select]");

        tabs.forEach((tab, index) => {
          tab.addEventListener("click", () => activateDestination(modePanel, tab));

          tab.addEventListener("keydown", (event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowRight") {
              event.preventDefault();
              const nextTab = tabs[(index + 1) % tabs.length];
              if (nextTab) activateDestination(modePanel, nextTab, { focus: true });
            } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
              event.preventDefault();
              const nextTab = tabs[(index - 1 + tabs.length) % tabs.length];
              if (nextTab) activateDestination(modePanel, nextTab, { focus: true });
            } else if (event.key === "Home") {
              event.preventDefault();
              if (tabs[0]) activateDestination(modePanel, tabs[0], { focus: true });
            } else if (event.key === "End") {
              event.preventDefault();
              if (tabs[tabs.length - 1]) activateDestination(modePanel, tabs[tabs.length - 1], { focus: true });
            }
          });
        });

        if (select) {
          select.addEventListener("change", () => {
            const nextTab = tabs.find((tab) => tab.getAttribute("aria-controls") === select.value);
            if (nextTab) activateDestination(modePanel, nextTab);
          });
        }
      });

      launcherButtons.forEach((launcher) => {
        const parentMenu = launcher.closest(".submenu-container");
        if (parentMenu) parentMenu.classList.add("sa-solution-launcher-parent");

        ["mouseenter", "pointerenter", "mouseover", "mousemove"].forEach((eventName) => {
          launcher.addEventListener(eventName, () => scheduleOverlayOpen(launcher));
        });
        launcher.addEventListener("focus", () => {
          if (isDesktop()) openOverlay(launcher);
        });
        launcher.addEventListener("click", (event) => {
          event.preventDefault();
          if (navigator.classList.contains("is-overlay-open") && activeLauncher === launcher && isLockedOpen) {
            closeOverlay({ focusLauncher: true });
          } else {
            openOverlay(launcher, { lock: true });
          }
        });
        launcher.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openOverlay(launcher, { lock: true });
          } else if (event.key === "Escape") {
            event.preventDefault();
            closeOverlay({ focusLauncher: true });
          }
        });
      });

      const previewRegions = Array.from(new Set(previewLaunchers.map((launcher) => launcher.closest(".sa-hero__nav")).filter(Boolean)));

      previewLaunchers.forEach((launcher) => {
        ["mouseenter", "pointerenter", "mouseover", "mousemove"].forEach((eventName) => {
          launcher.addEventListener(eventName, () => scheduleOverlayOpen(launcher));
        });
        launcher.addEventListener("focus", () => {
          if (isDesktop()) openOverlay(launcher);
        });
        launcher.addEventListener("click", (event) => {
          if (!isPreviewActionButton(launcher)) return;
          event.preventDefault();
          openOverlay(launcher, { lock: true });
        });
        launcher.addEventListener("keydown", (event) => {
          if (isPreviewActionButton(launcher) && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            openOverlay(launcher, { lock: true });
          } else if (event.key === "Escape") {
            event.preventDefault();
            closeOverlay({ focusLauncher: true });
          }
        });
      });

      previewRegions.forEach((region) => {
        region.addEventListener("mouseenter", () => window.clearTimeout(closeTimer));
        region.addEventListener("pointerenter", () => window.clearTimeout(closeTimer));
        region.addEventListener("mouseleave", () => scheduleOverlayClose());
        region.addEventListener("focusout", () => {
          window.setTimeout(() => {
            if (!region.contains(document.activeElement) && !navigator.contains(document.activeElement)) {
              scheduleOverlayClose();
            }
          }, 0);
        });
      });

      navigator.addEventListener("mouseenter", () => window.clearTimeout(closeTimer));
      navigator.addEventListener("pointerenter", () => window.clearTimeout(closeTimer));
      navigator.addEventListener("mouseleave", () => scheduleOverlayClose());
      navigator.addEventListener("focusin", () => window.clearTimeout(closeTimer));
      navigator.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", () => closeOverlay());
      });
      document.addEventListener("click", (event) => {
        const link = event.target && event.target.closest ? event.target.closest("a[href]") : null;
        if (!isShopByIndustryAnchor(link)) return;
        if (link.matches("[data-sa-solution-preview], [data-sa-solution-launcher]")) return;
        suppressPreviewHoverUntil = Date.now() + 1200;
        window.clearTimeout(openTimer);
        if (link.closest("header-drawer")) {
          event.preventDefault();
          window.history.pushState({}, "", "#sa-shop-by-industry");
          closeHeaderDrawerForAnchor(link);
          window.setTimeout(focusShopByIndustrySection, 450);
        }
      }, true);
      closeButtons.forEach((button) => {
        button.addEventListener("click", () => closeOverlay({ focusLauncher: true }));
      });
      document.addEventListener("click", (event) => {
        if (!navigator.classList.contains("is-overlay-open")) return;
        if (navigator.contains(event.target)) return;
        if (overlayTriggers.some((launcher) => launcher.contains(event.target))) return;
        closeOverlay();
      });
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && navigator.classList.contains("is-overlay-open")) {
          event.preventDefault();
          closeOverlay({ focusLauncher: true });
        }
      });
      window.addEventListener("resize", () => {
        if (navigator.classList.contains("is-overlay-open")) positionOverlay();
      });

      const hashTarget = window.location.hash ? window.location.hash.slice(1) : "";
      const normalizedHashTarget = hashTarget ? hashTarget.replace(/^sa-/, "sa-solution-") : "";
      const hashPanel = normalizedHashTarget ? navigator.querySelector(`#${escapeSelectorId(normalizedHashTarget)}`) : null;
      if (hashPanel) {
        const containingModePanel = hashPanel.closest("[data-sa-solution-mode-panel]");
        const modeTab = modeTabs.find((tab) => tab.getAttribute("aria-controls") === containingModePanel.id);
        const destinationTab = containingModePanel.querySelector(`[aria-controls="${escapeSelectorId(hashPanel.id)}"]`);
        if (modeTab) activateMode(modeTab);
        if (destinationTab) activateDestination(containingModePanel, destinationTab, { updateHash: false });
      } else {
        const selectedMode = modeTabs.find((tab) => tab.getAttribute("aria-selected") === "true") || modeTabs[0];
        activateMode(selectedMode);
      }

      closeOverlay();
    });
  };

  const initProductTabs = (root = document) => {
    root.querySelectorAll("[data-sa-product-tabs]").forEach((tabsRegion) => {
      if (tabsRegion.dataset.saProductReady === "true") return;

      const tabs = Array.from(tabsRegion.querySelectorAll("[data-sa-product-tab]"));
      const panels = Array.from(tabsRegion.querySelectorAll("[data-sa-product-panel]"));
      const viewAllLink = tabsRegion.querySelector("[data-sa-product-view-all]");
      if (!tabs.length || !panels.length) return;

      tabsRegion.dataset.saProductReady = "true";

      const activateTab = (tab, { focus = false } = {}) => {
        const panelId = tab.getAttribute("aria-controls");

        tabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle("is-active", isActive);
          candidate.setAttribute("aria-selected", isActive ? "true" : "false");
          candidate.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;

          if (isActive) {
            const track = panel.querySelector("[data-sa-carousel-track]");
            if (track) track.scrollTo({ left: 0, behavior: "auto" });
          }
        });

        if (viewAllLink && tab.dataset.saProductUrl) {
          viewAllLink.href = tab.dataset.saProductUrl;
        }

        if (focus) tab.focus();
      };

      const moveTab = (targetIndex) => {
        const nextTab = tabs[(targetIndex + tabs.length) % tabs.length];
        if (nextTab) activateTab(nextTab, { focus: true });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => activateTab(tab));

        tab.addEventListener("keydown", (event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveTab(index + 1);
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveTab(index - 1);
          } else if (event.key === "Home") {
            event.preventDefault();
            moveTab(0);
          } else if (event.key === "End") {
            event.preventDefault();
            moveTab(tabs.length - 1);
          }
        });
      });

      const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
      activateTab(selectedTab);
    });
  };

  const initProductCarousels = (root = document) => {
    root.querySelectorAll("[data-sa-product-carousel]").forEach((carousel) => {
      if (carousel.dataset.saCarouselReady === "true") return;

      const track = carousel.querySelector("[data-sa-carousel-track]");
      const prev = carousel.querySelector("[data-sa-carousel-prev]");
      const next = carousel.querySelector("[data-sa-carousel-next]");
      if (!track || !prev || !next) return;

      carousel.dataset.saCarouselReady = "true";

      const scrollByPage = (direction) => {
        const amount = Math.max(220, track.clientWidth * 0.86);
        track.scrollBy({ left: amount * direction, behavior: "smooth" });
      };

      prev.addEventListener("click", () => scrollByPage(-1));
      next.addEventListener("click", () => scrollByPage(1));
    });
  };

  const initShopByIndustryDrawerAnchors = () => {
    if (document.documentElement.dataset.saShopByIndustryDrawerAnchorReady === "true") return;
    document.documentElement.dataset.saShopByIndustryDrawerAnchorReady = "true";

    const isSamePageIndustryAnchor = (link) => {
      if (!link || !link.matches || !link.matches("a[href]")) return false;
      try {
        const target = new URL(link.getAttribute("href"), window.location.href);
        return target.origin === window.location.origin && target.pathname === window.location.pathname && target.hash === "#sa-shop-by-industry";
      } catch (error) {
        return false;
      }
    };

    const focusIndustrySection = () => {
      const target = document.getElementById("sa-shop-by-industry");
      if (!target) return;
      if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      target.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start"
      });
      target.focus({ preventScroll: true });
    };

    const closeDrawer = (headerDrawer) => {
      if (!headerDrawer) return;
      const scrollTop = Number(document.body.getAttribute("data-top")) || window.pageYOffset || 0;
      headerDrawer.querySelectorAll("details[open]").forEach((details) => {
        details.classList.remove("menu-opening", "submenu-open");
        const summary = details.querySelector("summary");
        if (summary) summary.setAttribute("aria-expanded", "false");
        details.removeAttribute("open");
      });
      document.body.style.removeProperty("top");
      document.body.classList.remove("fixed-position", "overflow-hidden-tablet");
      document.querySelector(".section-header")?.classList.remove("menu-open");
      window.scrollTo(0, scrollTop);
    };

    document.addEventListener("click", (event) => {
      const link = event.target && event.target.closest ? event.target.closest("a[href]") : null;
      const headerDrawer = link && link.closest ? link.closest("header-drawer") : null;
      if (!headerDrawer || !isSamePageIndustryAnchor(link)) return;

      event.preventDefault();
      window.history.pushState({}, "", "#sa-shop-by-industry");
      closeDrawer(headerDrawer);
      window.setTimeout(focusIndustrySection, 450);
    }, true);
  };

  const initSaHomepage = (root = document) => {
    initShopByIndustryDrawerAnchors();
    hydrateCanonicalDestinationProducts(root);
    initIndustryHeroes(root);
    initSolutionNavigators(root);
    initBrandShowcases(root);
    initProductTabs(root);
    initProductCarousels(root);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initSaHomepage());
  } else {
    initSaHomepage();
  }

  document.addEventListener("shopify:section:load", (event) => initSaHomepage(event.target));
})();
